import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/lib/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Plus, Play, CheckCircle, MapPin, Clock, Trash2, Navigation,
  Droplets, Sparkles, AlertTriangle, Edit2, ArrowLeft, Users, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
// 📝 [Phase 104 — 2026-07-09] Badge SMS pour la vue admin d'une route de lavage
import { RouteLavageSmsHistoryBadge } from '@/components/routes-lavage/RouteLavageSmsHistoryBadge';
import { urlFichier } from '@/lib/fichiers';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';
const ADMIN_ROLES = ['proprietaire', 'charge_projet', 'adjoint_admin', 'representant', 'admin'];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const statutColor = (s) =>
  s === 'terminee' ? 'bg-green-100 text-green-800' :
  s === 'en_cours'  ? 'bg-blue-100 text-blue-800' :
  'bg-gray-100 text-gray-600';

const statutLabel = (s) =>
  s === 'terminee' ? 'Terminée' : s === 'en_cours' ? 'En cours' : 'Planifiée';

const fmtDate = (d) =>
  new Date(d + 'T12:00:00').toLocaleDateString('fr-CA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

// ─── Page principale ───────────────────────────────────────────────────────────
export function RoutesLavage() {
  const { user } = useAuth();
  const [routes, setRoutes] = useState([]);
  const [alertes, setAlertes] = useState([]);
  const [showCreer, setShowCreer] = useState(false);
  const [view, setView] = useState('list');   // 'list' | 'apercu' | 'execution'
  const [activeRoute, setActiveRoute] = useState(null);
  const [loading, setLoading] = useState(true);

  const location = useLocation();
  const isAdmin = user && ADMIN_ROLES.includes(user.role);
  const hasAutoSelected = useRef(false);

  useEffect(() => { fetchRoutes(); fetchAlertes(); }, []);

  // Auto-select route when navigated from Production Calendar
  useEffect(() => {
    const selectedRouteId = location.state?.selectedRouteId;
    if (selectedRouteId && routes.length > 0 && !activeRoute && !hasAutoSelected.current) {
      const found = routes.find(r => r.id === selectedRouteId);
      if (found) {
        hasAutoSelected.current = true;
        setActiveRoute(found);
        setView('apercu');
      }
    }
  }, [location.state, routes]);

  const fetchRoutes = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/crm/routes-lavage`);
      if (res.status === 200) { const d = res.data; setRoutes(d.routes || []); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchAlertes = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/crm/alertes-lavage`);
      if (res.status === 200) { const d = res.data; setAlertes(d.alertes || []); }
    } catch (e) { console.error(e); }
  };

  const refreshActiveRoute = async (id) => {
    try {
      const res = await axios.get(`${API_URL}/api/crm/routes-lavage/${id}`);
      if (res.status === 200) setActiveRoute(res.data);
    } catch (e) {
      console.error('Erreur chargement route:', e);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette route ?')) return;
    await axios.delete(`${API_URL}/api/crm/routes-lavage/${id}`);
    toast.success('Route supprimée');
    fetchRoutes();
  };

  // ── Vue exécution ──
  if (view === 'execution' && activeRoute) {
    return (
      <RouteEnCours
        route={activeRoute}
        onBack={async () => {
          await refreshActiveRoute(activeRoute.id);
          setView('apercu');
          fetchRoutes();
        }}
      />
    );
  }

  // ── Vue aperçu ──
  if (view === 'apercu' && activeRoute) {
    return (
      <RouteApercu
        route={activeRoute}
        isAdmin={isAdmin}
        onBack={() => { setView('list'); setActiveRoute(null); fetchRoutes(); }}
        onGoToExecution={() => setView('execution')}
        onRouteUpdated={(updated) => { setActiveRoute(updated); fetchRoutes(); }}
      />
    );
  }

  // ── Vue liste ──
  if (loading) return (
    <div className="p-6 flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
    </div>
  );

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold text-gray-900">Routes de Lavage</h1>
          <p className="text-gray-600 mt-1 text-sm">Planifier et suivre les routes de lavage à pression</p>
        </div>
        {isAdmin && (
          <p className="text-xs text-slate-500">La création de routes se fait depuis le Calendrier de Production.</p>
        )}
      </div>

      {/* Alertes lavage */}
      {alertes.length > 0 && (
        <Card className="mb-4 border-amber-200 bg-amber-50/30">
          <CardContent className="py-3">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-semibold text-amber-800">Lavages à planifier ({alertes.length})</span>
            </div>
            <div className="space-y-1">
              {alertes.map((a, i) => (
                <div key={a.id || a._id || `${JSON.stringify(a)}-${i}`} className="flex justify-between items-center text-sm px-2 py-1 bg-white rounded">
                  <span><strong>{a.client_nom}</strong> — {a.adresse}</span>
                  <Badge className={a.jours_restants <= 3 ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}>
                    {a.jours_restants === 0 ? "Aujourd'hui!" : `Dans ${a.jours_restants} jour${a.jours_restants > 1 ? 's' : ''}`}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ⚙️ Réparation des punches historiques orphelins (admin uniquement) */}
      {isAdmin && <ReparationOrphelinsCard />}

      {routes.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Droplets className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">Aucune route de lavage planifiée</p>
            {isAdmin && (
              <p className="text-sm text-slate-500">Créez des routes depuis le Calendrier de Production.</p>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {routes.map(route => {
            const done = route.clients?.filter(c => c.statut === 'termine').length || 0;
            const total = route.clients?.length || 0;
            return (
              <Card
                key={route.id}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => { setActiveRoute(route); setView('apercu'); }}
                data-testid={`route-card-${route.id}`}
              >
                <CardContent className="py-4 px-3 sm:px-6">
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="p-2.5 rounded-lg bg-blue-50 text-blue-600 shrink-0">
                        <Droplets className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-sm sm:text-base truncate">
                          Route du {new Date(route.date_route + 'T12:00:00').toLocaleDateString('fr-CA', { weekday: 'short', day: 'numeric', month: 'long' })}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-500 truncate">
                          {total} client{total > 1 ? 's' : ''} — {done}/{total} complété{done > 1 ? 's' : ''}
                          {route.employes?.length > 0 && (
                            <span className="ml-2 text-blue-600">· {route.employes.map(e => e.nom.split(' ')[0]).join(', ')}</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge className={statutColor(route.statut)}>{statutLabel(route.statut)}</Badge>
                      {isAdmin && (
                        <Button
                          variant="ghost" size="sm"
                          onClick={(e) => { e.stopPropagation(); handleDelete(route.id); }}
                          data-testid={`btn-delete-route-${route.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {showCreer && (
        <CreerRouteDialog
          open={showCreer}
          onClose={() => setShowCreer(false)}
          onCreated={() => { setShowCreer(false); fetchRoutes(); }}
        />
      )}
    </div>
  );
}

// ─── Vue Aperçu ───────────────────────────────────────────────────────────────
function RouteApercu({ route, isAdmin, onBack, onGoToExecution, onRouteUpdated }) {
  const [showModifier, setShowModifier] = useState(false);
  const [btPreview, setBtPreview] = useState(null);
  const [showBtDialog, setShowBtDialog] = useState(false);
  const clients = route.clients || [];
  const done = clients.filter(c => c.statut === 'termine').length;

  const voirBT = async (dossierId) => {
    try {
      // ✅ 2026-02-20 — Utilise l'endpoint `bt-preview` qui ne requiert pas
      // les permissions admin sur `/api/crm/bons-travail`. Ça permet aux peintres
      // / sous-traitants assignés à la route de voir le BT sans accès au dossier.
      // L'adresse est volontairement omise (côté backend).
      const res = await axios.get(`${API_URL}/api/crm/routes-lavage/${route.id}/bt-preview/${dossierId}`);
      if (res.status === 200) {
        setBtPreview(res.data);
        setShowBtDialog(true);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'BT introuvable');
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      {/* Header */}
      <Button variant="ghost" onClick={onBack} className="mb-4 text-sm -ml-2" data-testid="btn-retour-liste">
        <ArrowLeft className="w-4 h-4 mr-1" /> Retour aux routes
      </Button>

      {/* Fiche route */}
      <Card className="mb-4">
        <CardContent className="py-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-blue-50 text-blue-600">
                <Droplets className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">{fmtDate(route.date_route)}</h2>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <Badge className={statutColor(route.statut)}>{statutLabel(route.statut)}</Badge>
                  <span className="text-sm text-gray-500">{done}/{clients.length} client{clients.length > 1 ? 's' : ''} complété{done > 1 ? 's' : ''}</span>
                  {/* 🆕 [Phase 104 — Bug #4] Badge historique SMS pour admin/employés de la route */}
                  <RouteLavageSmsHistoryBadge routeId={route.id} />
                </div>
              </div>
            </div>
            {isAdmin && route.statut === 'planifiee' && (
              <Button
                variant="outline" size="sm"
                onClick={() => setShowModifier(true)}
                data-testid="btn-modifier-route"
                className="flex items-center gap-1.5 text-blue-700 border-blue-200 hover:bg-blue-50"
              >
                <Edit2 className="w-4 h-4" /> Modifier
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Équipe */}
      {route.employes?.length > 0 && (
        <Card className="mb-4">
          <CardContent className="py-4">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-blue-600" />
              <span className="font-semibold text-sm text-gray-700">Équipe assignée</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {route.employes.map((emp, i) => (
                <Badge key={emp.id || emp._id || `${JSON.stringify(emp)}-${i}`} variant="secondary" className="text-sm px-3 py-1">{emp.nom}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notes de la route — mise en avant pour l'équipe de lavage */}
      {route.notes && (
        <Card className="mb-4 border-2 border-amber-300 bg-amber-50 shadow-sm" data-testid="apercu-route-notes-card">
          <CardContent className="py-4 px-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <p className="text-sm font-bold text-amber-800 uppercase tracking-wide">Notes importantes pour l'équipe</p>
            </div>
            <p className="text-sm text-amber-900 whitespace-pre-wrap leading-relaxed" data-testid="apercu-route-notes">{route.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Liste des clients */}
      <Card className="mb-5">
        <CardContent className="py-4">
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-4 h-4 text-blue-600" />
            <span className="font-semibold text-sm text-gray-700">Clients ({clients.length})</span>
          </div>
          <div className="space-y-3">
            {clients.map((client, idx) => {
              const isDone = client.statut === 'termine';
              const showAddress = isAdmin || isDone;
              return (
                <div
                  key={client.id || idx}
                  className={`flex items-start gap-3 p-3 rounded-lg border ${isDone ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}
                  data-testid={`apercu-client-${idx}`}
                >
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${isDone ? 'bg-green-500 text-white' : 'bg-blue-100 text-blue-700'}`}>
                    {isDone ? <CheckCircle className="w-4 h-4" /> : idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-semibold text-sm ${isDone ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                      {client.client_nom}
                    </p>
                    {showAddress ? (
                      <a
                        href={`https://maps.google.com/?q=${encodeURIComponent(client.adresse)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-0.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MapPin className="w-3 h-3 shrink-0" />
                        {client.adresse}
                      </a>
                    ) : (
                      <p className="text-xs text-gray-400 italic mt-0.5">Adresse disponible à l'exécution</p>
                    )}
                    {isDone && client.duree_minutes && (
                      <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {client.duree_minutes} min
                      </p>
                    )}
                    {client.notes_lavage_pression && (
                      <div className="mt-2 bg-blue-50 border border-blue-200 rounded-md px-2 py-1.5" data-testid={`notes-lavage-pression-${idx}`}>
                        <p className="text-[10px] font-semibold text-blue-700 uppercase tracking-wide flex items-center gap-1">
                          <Droplets className="w-3 h-3" /> Notes lavage à pression
                        </p>
                        <p className="text-xs text-blue-900 mt-0.5 whitespace-pre-wrap">{client.notes_lavage_pression}</p>
                      </div>
                    )}
                    {/* ✅ 2026-02-20 — Afficher équipements spéciaux requis dès l'aperçu */}
                    {!isDone && (client.nacelle_lavage_requise || client.echafauds_a_monter || client.nettoyer_echelle) && (
                      <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`apercu-equipements-${idx}`}>
                        {client.nacelle_lavage_requise && (
                          <Badge className="bg-amber-100 text-amber-800 text-[10px] flex items-center gap-1">
                            🏗 Nacelle requise{client.nacelle_lavage_nom ? ` : ${client.nacelle_lavage_nom}` : ''}
                          </Badge>
                        )}
                        {client.echafauds_a_monter && (
                          <Badge className="bg-orange-100 text-orange-800 text-[10px]">🪜 Échafauds à monter</Badge>
                        )}
                        {client.nettoyer_echelle && (
                          <Badge className="bg-sky-100 text-sky-800 text-[10px]">
                            🧽 Nettoyer échelle{client.nettoyer_echelle_type_pieds ? ` (${client.nettoyer_echelle_type_pieds} pieds)` : ''}
                          </Badge>
                        )}
                      </div>
                    )}
                    {!isDone && client.dossier_id && (
                      <Button variant="ghost" size="sm" onClick={() => voirBT(client.dossier_id)}
                        className="mt-2 text-xs text-indigo-600 hover:bg-indigo-50 h-7 px-2" data-testid={`btn-voir-bt-apercu-${idx}`}>
                        <FileText className="w-3 h-3 mr-1" /> Voir le bon de travail complet
                      </Button>
                    )}
                  </div>
                  {isDone && <Badge className="bg-green-100 text-green-800 shrink-0">Terminé</Badge>}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Action : aller à l'exécution */}
      {route.statut !== 'terminee' && (
        <Button
          onClick={onGoToExecution}
          className="w-full h-12 bg-green-600 hover:bg-green-700 text-base"
          data-testid="btn-voir-execution"
        >
          <Play className="w-5 h-5 mr-2" />
          {route.statut === 'en_cours' ? "Continuer l'exécution" : 'Commencer la route'}
        </Button>
      )}
      {route.statut === 'terminee' && (
        <Button variant="outline" onClick={onGoToExecution} className="w-full" data-testid="btn-voir-detail-execution">
          Voir le détail d'exécution
        </Button>
      )}

      {/* Dialog modifier */}
      {showModifier && (
        <ModifierRouteDialog
          route={route}
          onClose={() => setShowModifier(false)}
          onUpdated={(updated) => { setShowModifier(false); onRouteUpdated(updated); }}
        />
      )}

      {/* ✅ 2026-02-20 — Dialog Bon de Travail (sans adresse) pour la vue Aperçu */}
      <Dialog open={showBtDialog} onOpenChange={setShowBtDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="bt-preview-apercu-dialog">
          <DialogHeader><DialogTitle>Bon de Travail — {btPreview?.client_nom}</DialogTitle></DialogHeader>
          {btPreview && (
            <div className="space-y-3">
              {(() => {
                const renderRich = (s) => {
                  if (!s) return '';
                  return /<[a-z][\s\S]*>/i.test(s) ? s : String(s).replace(/\r?\n/g, '<br>');
                };
                return (
                  <>
                    {btPreview.description_travaux && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase">Description des travaux</p>
                        <div className="text-sm mt-1 prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: renderRich(btPreview.description_travaux) }} />
                      </div>
                    )}
                    {btPreview.notes_speciales && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <p className="text-xs font-semibold text-amber-700">Notes spéciales</p>
                        <div className="text-sm text-amber-900 mt-0.5 prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: renderRich(btPreview.notes_speciales) }} />
                      </div>
                    )}
                    {btPreview.notes_lavage_pression && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-xs font-semibold text-blue-700">Notes lavage à pression</p>
                        <div className="text-sm text-blue-900 mt-0.5 prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: renderRich(btPreview.notes_lavage_pression) }} />
                      </div>
                    )}
                    {/* Équipements spéciaux (nacelle / échafauds / échelle) */}
                    {(btPreview.nacelle_lavage_requise || btPreview.echafauds_a_monter || btPreview.nettoyer_echelle) && (
                      <div className="flex flex-wrap gap-1.5">
                        {btPreview.nacelle_lavage_requise && (
                          <Badge className="bg-amber-100 text-amber-800 text-xs">
                            🏗 Nacelle requise{btPreview.nacelle_lavage_nom ? ` : ${btPreview.nacelle_lavage_nom}` : ''}
                          </Badge>
                        )}
                        {btPreview.echafauds_a_monter && (
                          <Badge className="bg-orange-100 text-orange-800 text-xs">🪜 Échafauds à monter</Badge>
                        )}
                        {btPreview.nettoyer_echelle && (
                          <Badge className="bg-sky-100 text-sky-800 text-xs">
                            🧽 Nettoyer échelle{btPreview.nettoyer_echelle_type_pieds ? ` (${btPreview.nettoyer_echelle_type_pieds} pi)` : ''}
                          </Badge>
                        )}
                      </div>
                    )}
                    {/* Photos du dossier */}
                    {btPreview.photos?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-1.5">Photos</p>
                        <div className="grid grid-cols-3 gap-1.5">
                          {btPreview.photos.slice(0, 9).map((ph) => (
                            <a key={ph.id || ph.url} href={urlFichier(ph.url)} target="_blank" rel="noopener noreferrer"
                              className="block aspect-square overflow-hidden rounded-md border border-gray-200">
                              <img src={urlFichier(ph.thumbnail_url || ph.url)} alt={ph.caption || ''}
                                className="w-full h-full object-cover hover:scale-105 transition-transform" />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
              {btPreview.zones?.length > 0 && (
                <div><p className="text-xs font-semibold text-gray-500 uppercase">Zones</p>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {btPreview.zones.map((z, i) => <Badge key={z.id || z.nom || `zone-${i}`} variant="outline" className="text-xs">{z.nom || z}</Badge>)}
                  </div></div>
              )}
              {btPreview.etapes?.length > 0 && (
                <div><p className="text-xs font-semibold text-gray-500 uppercase">Étapes</p>
                  <div className="space-y-1 mt-1">
                    {btPreview.etapes.map((et, i) => (
                      <div key={et.id || et._id || `et-${i}`} className="flex items-center gap-2 text-sm">
                        <span className="w-5 h-5 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-xs font-bold">{i+1}</span>
                        <span>{et.nom || et.description || et}</span>
                      </div>
                    ))}
                  </div></div>
              )}
              {btPreview.produits?.length > 0 && (
                <div><p className="text-xs font-semibold text-gray-500 uppercase">Produits</p>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {btPreview.produits.map((p, i) => <Badge key={p.id || p.nom || `produit-${i}`} variant="secondary" className="text-xs">{p.nom || p}</Badge>)}
                  </div></div>
              )}
              <div className="grid grid-cols-2 gap-3 bg-gray-50 rounded-lg p-3">
                <div><p className="text-xs text-gray-500">Heures budgétées</p><p className="text-lg font-bold">{btPreview.heures_budgetees || 0}h</p></div>
                <div><p className="text-xs text-gray-500">Heures réelles</p><p className="text-lg font-bold">{btPreview.heures_reelles || 0}h</p></div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Dialog Modifier Route ────────────────────────────────────────────────────
function ModifierRouteDialog({ route, onClose, onUpdated }) {
  const [date, setDate] = useState(route.date_route);
  const [dossiers, setDossiers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState(route.clients.map(c => ({ id: c.dossier_id, client_nom: c.client_nom, adresse_travaux: c.adresse?.split(',')[0] || '', ville_travaux: c.ville || '' })));
  const [selectedEmployees, setSelectedEmployees] = useState(route.employes || []);
  const [notes, setNotes] = useState(route.notes || '');
  const [saving, setSaving] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [searchClient, setSearchClient] = useState('');

  useEffect(() => { fetchDossiers(); fetchEmployees(); }, []);

  const fetchDossiers = async () => {
    try {
      const [dosRes, btRes] = await Promise.all([
        axios.get(`${API_URL}/api/crm/dossiers`),
        axios.get(`${API_URL}/api/crm/bons-travail`),
      ]);
      const allDossiers = dosRes.data?.dossiers || [];
      const bonsExt = (btRes.data?.bons_travail || []).filter(b => b.type_travaux === 'exterieur' || b.lavage_pression_a_faire);
      // Index: dossier_id → notes_lavage_pression du BT
      const notesLavageMap = {};
      for (const b of bonsExt) {
        if (b.notes_lavage_pression && b.dossier_id) {
          notesLavageMap[b.dossier_id] = b.notes_lavage_pression;
        }
      }
      const dossierIdsWithBonExt = new Set(bonsExt.map(b => b.dossier_id));
      const STATUTS_SIGNES = ['accepte', 'en_attente_production', 'en_production', 'finalise'];
      const filtered = allDossiers
        .filter(d => STATUTS_SIGNES.includes(d.statut) && dossierIdsWithBonExt.has(d.id))
        .map(d => ({ ...d, notes_lavage_pression: notesLavageMap[d.id] || null }));
      setDossiers(filtered);
    } catch (e) { console.error(e); }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/employees`);
      if (res.status === 200) { const d = res.data; setEmployees((d.employees || []).filter(e => e.est_actif)); }
    } catch (e) { console.error(e); }
  };

  const toggleDossier = (dossier) => {
    setSelected(prev => prev.find(d => d.id === dossier.id)
      ? prev.filter(d => d.id !== dossier.id)
      : [...prev, dossier]);
  };

  const toggleEmployee = (emp) => {
    const mapped = { id: emp.id, nom: `${emp.prenom} ${emp.nom}` };
    setSelectedEmployees(prev => prev.find(e => e.id === emp.id)
      ? prev.filter(e => e.id !== emp.id)
      : [...prev, mapped]);
  };

  const optimiserRoute = async () => {
    if (selected.length < 2) { toast.error('Sélectionnez au moins 2 clients'); return; }
    setOptimizing(true);
    try {
      const res = await axios.post(`${API_URL}/api/crm/routes-lavage/optimiser`, {
        clients: selected.map(d => ({ 
          client_nom: d.client_nom, 
          adresse: `${d.adresse_travaux || ''}, ${d.ville_travaux || ''}`, 
          id: d.id, 
          notes_lavage_pression: d.notes_lavage_pression || null 
        })),
      });
      if (res.status === 200) {
        const data = res.data;
        if (data.ordre_optimise?.length) {
          const optimised = data.ordre_optimise.map(c => selected.find(s => s.id === c.id) || c).filter(Boolean);
          if (optimised.length === selected.length) { 
            setSelected(optimised); 
            
            // Afficher stats d'optimisation
            const stats = data.stats || {};
            const statsMsg = [
              '✅ Route optimisée !',
              stats.distance_totale_km ? `📍 Distance: ${stats.distance_totale_km} km` : '',
              stats.duree_totale_min ? `⏱️ Temps: ${Math.floor(stats.duree_totale_min / 60)}h ${stats.duree_totale_min % 60}min` : '',
              stats.retours_bureau !== undefined ? `🔄 Retours bureau: ${stats.retours_bureau}` : ''
            ].filter(Boolean).join('\n');
            
            toast.success(statsMsg, { duration: 5000 });
            
            // Afficher justification si disponible
            if (stats.justification) {
              console.log('🤖 IA Justification:', stats.justification);
            }
          }
        }
      }
    } catch (err) { 
      console.error('Optimisation error:', err);
      toast.error('Erreur optimisation'); 
    }
    finally { setOptimizing(false); }
  };

  const handleSave = async () => {
    if (selected.length === 0) { toast.error('Sélectionnez au moins un client'); return; }
    setSaving(true);
    try {
      const payload = {
        date_route: date,
        employes: selectedEmployees,
        notes: notes,
        clients: selected.map((d, i) => ({
          dossier_id: d.id,
          client_nom: d.client_nom,
          adresse: `${d.adresse_travaux || ''}, ${d.ville_travaux || ''}`,
          ville: d.ville_travaux || '',
          ordre: i,
          statut: 'en_attente',
          notes_lavage_pression: d.notes_lavage_pression || null,
        })),
      };
      const res = await axios.put(`${API_URL}/api/crm/routes-lavage/${route.id}`, payload);
      if (res.status !== 200) throw new Error();
      const updated = res.data;
      toast.success('Route modifiée!');
      onUpdated(updated);
    } catch (_) { toast.error('Erreur lors de la modification'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit2 className="w-5 h-5 text-blue-600" /> Modifier la Route de Lavage
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Date de la route</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div>
            <Label className="mb-2 block">Employés assignés</Label>
            <div className="flex flex-wrap gap-2">
              {employees.map(emp => {
                const isSel = selectedEmployees.find(e => e.id === emp.id);
                return (
                  <button
                    key={emp.id} type="button"
                    onClick={() => toggleEmployee(emp)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${isSel ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'}`}
                  >
                    {emp.prenom} {emp.nom}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label>Notes pour les employés</Label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Instructions, consignes de sécurité, matériel requis..."
              className="w-full mt-1 px-3 py-2 border rounded-lg text-sm min-h-[60px] resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="input-notes-modifier-route"
            />
          </div>
          <div>
            <Label>Clients ({selected.length} sélectionné{selected.length > 1 ? 's' : ''})</Label>
            <Input
              className="mt-2 mb-2"
              placeholder="Rechercher un client..."
              value={searchClient}
              onChange={e => setSearchClient(e.target.value)}
              data-testid="input-search-client-modifier"
            />
            <div className="space-y-2 mt-2 max-h-[280px] overflow-y-auto">
              {dossiers.filter(d => {
                if (!searchClient.trim()) return true;
                const q = searchClient.toLowerCase();
                return (d.client_nom || '').toLowerCase().includes(q) || (d.adresse_travaux || '').toLowerCase().includes(q);
              }).map(d => {
                const isSel = selected.find(s => s.id === d.id);
                return (
                  <div
                    key={d.id}
                    className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${isSel ? 'bg-blue-50 border-blue-300' : 'hover:bg-gray-50'}`}
                    onClick={() => toggleDossier(d)}
                  >
                    <Checkbox checked={!!isSel} />
                    <div className="flex-1">
                      <p className="font-medium text-sm">{d.client_nom}</p>
                      <p className="text-xs text-gray-500">{d.adresse_travaux}, {d.ville_travaux}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex justify-between items-center pt-3 border-t">
            <div>
              {selected.length >= 2 && (
                <Button variant="outline" onClick={optimiserRoute} disabled={optimizing} className="text-purple-700 border-purple-200 hover:bg-purple-50">
                  <Sparkles className="w-4 h-4 mr-1" />
                  {optimizing ? 'Optimisation en cours...' : 'Optimiser par IA'}
                </Button>
              )}
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={onClose}>Annuler</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-save-modifier-route">
                {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Dialog Créer Route ───────────────────────────────────────────────────────
function CreerRouteDialog({ open, onClose, onCreated }) {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [dossiers, setDossiers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [searchClient, setSearchClient] = useState('');

  useEffect(() => { fetchClients(); fetchEmployees(); }, []);

  const fetchClients = async () => {
    try {
      // Utiliser le nouvel endpoint qui retourne seulement les clients avec lavage coché
      const res = await axios.get(`${API_URL}/api/crm/lavages-pression-clients`);
      if (res.status === 200) {
        const data = res.data;
        setDossiers((data.clients || []).map(c => ({
          id: c.dossier_id,
          client_nom: c.client_nom,
          adresse_travaux: c.adresse?.split(',')[0] || '',
          ville_travaux: c.adresse?.split(',')[1]?.trim() || '',
          premier_jour_chantier: c.premier_jour_chantier,
        })));
      }
    } catch (e) { console.error(e); }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/employees`);
      if (res.status === 200) { const d = res.data; setEmployees((d.employees || []).filter(e => e.est_actif)); }
    } catch (e) { console.error(e); }
  };

  const toggleDossier = (dossier) => {
    setSelected(prev => prev.find(d => d.id === dossier.id)
      ? prev.filter(d => d.id !== dossier.id)
      : [...prev, dossier]);
  };

  const toggleEmployee = (emp) => {
    setSelectedEmployees(prev => prev.find(e => e.id === emp.id)
      ? prev.filter(e => e.id !== emp.id)
      : [...prev, emp]);
  };

  const optimiserRoute = async () => {
    if (selected.length < 2) { toast.error('Sélectionnez au moins 2 clients pour optimiser'); return; }
    setOptimizing(true);
    try {
      const res = await axios.post(`${API_URL}/api/crm/routes-lavage/optimiser`, {
        clients: selected.map(d => ({ client_nom: d.client_nom, adresse: `${d.adresse_travaux || ''}, ${d.ville_travaux || ''}`, id: d.id, notes_lavage_pression: d.notes_lavage_pression || null })),
      });
      if (res.status === 200) {
        const data = res.data;
        if (data.ordre_optimise?.length) {
          const optimised = data.ordre_optimise.map(c => selected.find(s => s.id === c.id) || c).filter(Boolean);
          if (optimised.length === selected.length) { setSelected(optimised); toast.success('Route optimisée par IA!'); }
        }
      }
    } catch (_) { toast.error('Erreur optimisation'); }
    finally { setOptimizing(false); }
  };

  const handleSave = async () => {
    if (selected.length === 0) { toast.error('Sélectionnez au moins un client'); return; }
    setSaving(true);
    try {
      const payload = {
        date_route: date,
        employes: selectedEmployees.map(e => ({ id: e.id, nom: `${e.prenom} ${e.nom}` })),
        notes: notes,
        clients: selected.map((d, i) => ({
          dossier_id: d.id,
          client_nom: d.client_nom,
          adresse: `${d.adresse_travaux || ''}, ${d.ville_travaux || ''}`,
          ville: d.ville_travaux || '',
          ordre: i,
        })),
      };
      const res = await axios.post(`${API_URL}/api/crm/routes-lavage`, payload);
      if (res.status !== 200) throw new Error();
      toast.success('Route de lavage créée!');
      onCreated();
    } catch (_) { toast.error('Erreur création'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Droplets className="w-5 h-5 text-blue-600" /> Créer une Route de Lavage
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Date de la route</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div>
            <Label className="flex items-center gap-2 mb-2">
              Employés assignés ({selectedEmployees.length} sélectionné{selectedEmployees.length > 1 ? 's' : ''})
            </Label>
            <div className="flex flex-wrap gap-2">
              {employees.map(emp => {
                const isSel = selectedEmployees.find(e => e.id === emp.id);
                return (
                  <button
                    key={emp.id} type="button" onClick={() => toggleEmployee(emp)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${isSel ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'}`}
                  >
                    {emp.prenom} {emp.nom}
                  </button>
                );
              })}
              {employees.length === 0 && <p className="text-sm text-gray-500 italic">Aucun employé actif trouvé</p>}
            </div>
          </div>
          <div>
            <Label>Notes pour les employés</Label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Instructions, consignes de sécurité, matériel requis..."
              className="w-full mt-1 px-3 py-2 border rounded-lg text-sm min-h-[60px] resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="input-notes-route"
            />
          </div>
          <div>
            <Label>Clients à laver ({selected.length} sélectionné{selected.length > 1 ? 's' : ''})</Label>
            <Input
              className="mt-2 mb-2"
              placeholder="Rechercher un client..."
              value={searchClient}
              onChange={e => setSearchClient(e.target.value)}
              data-testid="input-search-client-lavage"
            />
            <div className="space-y-2 mt-2 max-h-[300px] overflow-y-auto">
              {dossiers.filter(d => {
                if (!searchClient.trim()) return true;
                const q = searchClient.toLowerCase();
                return (d.client_nom || '').toLowerCase().includes(q) || (d.adresse_travaux || '').toLowerCase().includes(q) || (d.numero_dossier || '').toLowerCase().includes(q);
              }).map(d => {
                const isSel = selected.find(s => s.id === d.id);
                return (
                  <div
                    key={d.id}
                    className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${isSel ? 'bg-blue-50 border-blue-300' : 'hover:bg-gray-50'}`}
                    onClick={() => toggleDossier(d)}
                  >
                    <Checkbox checked={!!isSel} />
                    <div className="flex-1">
                      <p className="font-medium text-sm">{d.client_nom}</p>
                      <p className="text-xs text-gray-500">{d.adresse_travaux}, {d.ville_travaux} — {d.numero_dossier}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex justify-between items-center pt-3 border-t">
            <div>
              {selected.length >= 2 && (
                <Button variant="outline" onClick={optimiserRoute} disabled={optimizing} className="text-purple-700 border-purple-200 hover:bg-purple-50">
                  <Sparkles className="w-4 h-4 mr-1" /> {optimizing ? 'Optimisation...' : 'Optimiser la route (IA)'}
                </Button>
              )}
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={onClose}>Annuler</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-save-creer-route">
                {saving ? 'Création...' : 'Créer la Route'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Vue exécution (RouteEnCours) ─────────────────────────────────────────────
function RouteEnCours({ route: initialRoute, onBack }) {
  const { user } = useAuth();
  const [route, setRoute] = useState(initialRoute);
  const [loading, setLoading] = useState(false);
  const [btPreview, setBtPreview] = useState(null);
  const [showBtDialog, setShowBtDialog] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [now, setNow] = useState(Date.now());
  // 🆕 [5.5] Prompt confirmation nacelle retournée à la fin de la dernière étape
  const [nacelleRetourPrompt, setNacelleRetourPrompt] = useState({ open: false, routeId: null });

  const isAdmin = user && ADMIN_ROLES.includes(user.role);
  const employes = route.employes || [];
  const employesPrets = route.employes_prets || [];
  const myEmployeeId = user?.employee_id || user?.id;
  // 🆕 [Phase 104 — Bug #2] Détecte si le profil user n'est pas encore chargé.
  // Si false, on désactive les boutons "Démarrer" avec un message clair.
  const employeIdResolved = Boolean(myEmployeeId);

  // Ticking clock for the "waiting" timer
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 10000);
    return () => clearInterval(interval);
  }, []);

  // Track when current user first clicked "commencer"
  useEffect(() => {
    if (employesPrets.length > 0 && !startedAt) {
      setStartedAt(Date.now());
    }
  }, [employesPrets.length]);

  const refresh = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/crm/routes-lavage/${route.id}`);
      if (res.status === 200) setRoute(res.data);
    } catch (e) {
      console.error('Erreur rechargement route:', e);
    }
  };

  // Poll for updates while waiting for colleagues
  useEffect(() => {
    const allReady = employes.length === 0 || employes.every(e => employesPrets.includes(e.id));
    if (route.statut === 'planifiee' && employesPrets.length > 0 && !allReady) {
      const poll = setInterval(refresh, 5000);
      return () => clearInterval(poll);
    }
  }, [route.statut, employesPrets.length, employes.length]);

  const commencerRoute = async (empId) => {
    // 🆕 [Phase 104 — Bug #2] Guard : profil non résolu → message clair, pas d'appel API.
    if (!empId && !myEmployeeId) {
      toast.error("Ton profil employé n'est pas chargé. Recharge la page ou reconnecte-toi.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/crm/routes-lavage/${route.id}/commencer`, {
        employee_id: empId || myEmployeeId,
      });
      if (res.data?.all_ready) {
        toast.success('Route commencée !');
      } else {
        toast.success('Punch démarré ! En attente des collègues...');
      }
      // 🆕 [Phase v13.60.16 — 2026-08-18] Warning explicite si le pré-punch lavage
      // a été skippé à cause d'un punch en cours (avant : silent-skip côté backend).
      if (res.data?.prepunch_skipped && res.data?.message_info) {
        toast.info(res.data.message_info, { duration: 8000 });
      }
      await refresh();
    } catch (e) {
      // 🆕 [Phase 104 — Bug #2] Toast détaillé au lieu de "Erreur" générique.
      const detail = e?.response?.data?.detail || 'Erreur inconnue — voir la console';
      // eslint-disable-next-line no-console
      console.error('[commencerRoute]', e);
      toast.error(detail);
    }
    finally { setLoading(false); }
  };

  const commencerSolo = async () => {
    if (!myEmployeeId) {
      toast.error("Ton profil employé n'est pas chargé. Recharge la page ou reconnecte-toi.");
      return;
    }
    if (!window.confirm("Confirmer que votre collègue est absent et commencer la route seul ?")) return;
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/crm/routes-lavage/${route.id}/commencer-solo`, {
        employee_id: myEmployeeId,
      });
      toast.success('Route commencée en solo !');
      await refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Erreur inconnue — voir la console';
      // eslint-disable-next-line no-console
      console.error('[commencerSolo]', e);
      toast.error(detail);
    }
    finally { setLoading(false); }
  };

  const commencerNettoyage = async (idx) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/crm/routes-lavage/${route.id}/commencer-nettoyage/${idx}`);
      if (res.status === 200) {
        toast.success(`Nettoyage commencé — ${res.data.adresse}`);
        // 🆕 [Phase 104 — Bug #3] + [Phase v13.60.16 — 2026-08-18] Info discret si un
        // punch a été fermé auto. Message contextualisé selon le type de punch fermé.
        if (res.data?.punch_ferme_auto) {
          const ferme = (res.data?.punchs_admin_fermes_auto || [])[0];
          const prevContrat = ferme?.previous_contrat_id || '';
          const isBt = prevContrat && !prevContrat.startsWith('lavage-') && prevContrat !== 'admin_hours';
          const msg = isBt
            ? "Ton punch sur le bon de travail a été fermé automatiquement. Tu peux le rouvrir après la route si nécessaire."
            : "Ton punch admin a été fermé automatiquement. N'oublie pas de le rouvrir après la route si nécessaire.";
          toast.info(msg, { duration: 6000 });
        }
      }
      await refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Erreur inconnue — voir la console';
      // eslint-disable-next-line no-console
      console.error('[commencerNettoyage]', e);
      toast.error(detail);
    }
    finally { setLoading(false); }
  };

  const terminerNettoyage = async (idx) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/crm/routes-lavage/${route.id}/terminer-nettoyage/${idx}`);
      if (res.status === 200) {
        if (res.data.all_done) {
          toast.success('Route terminée !');
          // 🆕 [5.5] Confirmation nacelle retournée (uniquement si la route avait une nacelle assignée)
          if (route.nacelle_assignee_id) {
            setNacelleRetourPrompt({ open: true, routeId: route.id });
          }
        } else {
          toast.success('Nettoyage terminé ! Prochain client disponible.');
        }
      }
      await refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Erreur inconnue — voir la console';
      // eslint-disable-next-line no-console
      console.error('[terminerNettoyage]', e);
      toast.error(detail);
    }
    finally { setLoading(false); }
  };

  // 🆕 [5.5] Confirmation nacelle retournée — appelle l'endpoint qui cascade sur BT du dossier
  const confirmerNacelleRetour = async (retournee) => {
    const routeId = nacelleRetourPrompt.routeId;
    setNacelleRetourPrompt({ open: false, routeId: null });
    if (!retournee) {
      toast.info("Pense à marquer la nacelle plus tard depuis la fiche du BT ou la route");
      return;
    }
    try {
      await axios.post(`${API_URL}/api/crm/routes-lavage/${routeId}/nacelle-retournee`, {
        retournee_le: new Date().toISOString(),
      });
      toast.success("Nacelle marquée comme retournée — SMS de rappel arrêtés");
      await refresh();
    } catch (e) {
      toast.error("Erreur lors du marquage de la nacelle");
    }
  };

  // 🆕 Phase C5 (2026-05-04) — Marquer un client comme "non réalisé finalement".
  // Le client revient dans "Lavages à planifier" ; les heures punchées sont conservées.
  const marquerNonRealise = async (idx, clientNom) => {
    const ok = window.confirm(
      `Marquer "${clientNom}" comme non réalisé ?\n\n` +
      `• Le client retournera dans "Lavages à planifier".\n` +
      `• Les heures déjà punchées par les employés seront conservées.\n` +
      `• Cette action est irréversible (vous devrez reprogrammer).`
    );
    if (!ok) return;
    const raison = window.prompt('Raison (optionnel) — ex: météo, accès bloqué, propriétaire absent...') || '';
    setLoading(true);
    try {
      const res = await axios.patch(
        `${API_URL}/api/crm/routes-lavage/${route.id}/client/${idx}/non-realise`,
        { raison }
      );
      if (res.status === 200) {
        toast.success(`"${clientNom}" remis dans "Lavages à planifier"`);
        await refresh();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors du marquage non réalisé');
    } finally {
      setLoading(false);
    }
  };

  const voirBT = async (dossierId) => {
    try {
      const res = await axios.get(`${API_URL}/api/crm/routes-lavage/${route.id}/bt-preview/${dossierId}`);
      if (res.status === 200) { setBtPreview(res.data); setShowBtDialog(true); }
    } catch (_) { toast.error('BT introuvable'); }
  };

  const clients = route.clients || [];
  const activeIdx = route.client_actif_idx ?? -1;
  const isFullyStarted = route.statut === 'en_cours' || route.statut === 'terminee';
  const allReady = employes.length === 0 || employes.every(e => employesPrets.includes(e.id));
  const iHaveStarted = employesPrets.includes(myEmployeeId);
  const waitingMinutes = startedAt ? Math.floor((now - startedAt) / 60000) : 0;
  const canGoSolo = waitingMinutes >= 5 && iHaveStarted && !allReady;

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <Button variant="ghost" onClick={onBack} className="mb-4 text-sm -ml-2">
        <ArrowLeft className="w-4 h-4 mr-1" /> Retour à l'aperçu
      </Button>

      <Card className="mb-4">
        <CardContent className="py-4">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-blue-50 text-blue-600"><Droplets className="w-6 h-6" /></div>
            <div className="flex-1">
              <h2 className="text-lg font-bold">Route de Lavage — Exécution</h2>
              <p className="text-sm text-gray-500">{fmtDate(route.date_route)}</p>
              {employes.length > 0 && (
                <p className="text-xs text-blue-600 mt-1 font-medium">
                  Équipe: {employes.map(e => e.nom).join(', ')}
                </p>
              )}
            </div>
            <Badge className={statutColor(route.statut)}>{statutLabel(route.statut)}</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Notes visibles par les employés */}
      {route.notes && (
        <Card className="mb-4 border-amber-200 bg-amber-50">
          <CardContent className="py-3 px-4">
            <p className="text-xs font-semibold text-amber-700 mb-1">Notes de la route :</p>
            <p className="text-sm text-amber-900 whitespace-pre-wrap" data-testid="route-notes">{route.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* ── État: pas encore commencé ── */}
      {!isFullyStarted && (
        <div className="space-y-3 mb-4">
          {/* Admin: peut démarrer pour chaque employé */}
          {isAdmin && employes.length > 0 && (
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="py-4">
                <p className="text-sm font-semibold text-blue-800 mb-3">Confirmation des employés :</p>
                <div className="space-y-2">
                  {employes.map(emp => {
                    const isReady = employesPrets.includes(emp.id);
                    return (
                      <div key={emp.id} className={`flex items-center justify-between p-3 rounded-lg border ${isReady ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'}`}>
                        <div className="flex items-center gap-2">
                          {isReady ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Clock className="w-4 h-4 text-gray-400" />}
                          <span className={`text-sm font-medium ${isReady ? 'text-green-700' : 'text-gray-700'}`}>{emp.nom}</span>
                        </div>
                        {!isReady && (
                          <Button size="sm" onClick={() => commencerRoute(emp.id)} disabled={loading}
                            className="bg-blue-600 hover:bg-blue-700 text-xs" data-testid={`btn-start-emp-${emp.id}`}>
                            Démarrer pour {emp.nom.split(' ')[0]}
                          </Button>
                        )}
                        {isReady && <span className="text-xs text-green-600 font-medium">Prêt</span>}
                      </div>
                    );
                  })}
                </div>
                {allReady && employes.length > 0 && (
                  <p className="text-xs text-green-600 font-semibold mt-2 text-center">Tous prêts — la route va commencer !</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Employé: bouton "Commencer la route" */}
          {!isAdmin && !iHaveStarted && (
            <>
              {!employeIdResolved && (
                <div
                  className="mb-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 flex items-center justify-between gap-2"
                  data-testid="employe-profile-unresolved-warning"
                >
                  <span>Ton profil employé n&apos;est pas chargé. Recharge la page ou reconnecte-toi pour démarrer.</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.location.reload()}
                    data-testid="btn-refetch-profile"
                  >
                    Recharger
                  </Button>
                </div>
              )}
              <Button
                onClick={() => commencerRoute()}
                disabled={loading || !employeIdResolved}
                className="w-full h-14 text-lg bg-green-600 hover:bg-green-700"
                data-testid="btn-commencer-route"
              >
                <Play className="w-5 h-5 mr-2" /> Commencer la Route
              </Button>
            </>
          )}

          {/* En attente des collègues */}
          {iHaveStarted && !allReady && !isAdmin && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="py-4 text-center">
                <div className="animate-pulse mb-2">
                  <Clock className="w-8 h-8 mx-auto text-amber-600" />
                </div>
                <p className="text-sm font-semibold text-amber-800">En attente de vos collègues...</p>
                <div className="flex flex-wrap justify-center gap-2 mt-2">
                  {employes.filter(e => !employesPrets.includes(e.id)).map(e => (
                    <Badge key={e.id} variant="outline" className="text-amber-700 border-amber-300">{e.nom}</Badge>
                  ))}
                </div>
                {waitingMinutes > 0 && <p className="text-xs text-amber-600 mt-2">Attente depuis {waitingMinutes} min</p>}
                {canGoSolo && (
                  <Button onClick={commencerSolo} disabled={loading} variant="outline"
                    className="mt-3 text-red-700 border-red-300 hover:bg-red-50" data-testid="btn-solo">
                    Collègue absent, commencer la route seul
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Admin sans employés assignés → démarrage direct */}
          {isAdmin && employes.length === 0 && (
            <Button onClick={() => commencerRoute()} disabled={loading} className="w-full h-14 text-lg bg-green-600 hover:bg-green-700" data-testid="btn-commencer-route">
              <Play className="w-5 h-5 mr-2" /> Commencer la Route
            </Button>
          )}
        </div>
      )}

      {/* ── Liste des clients ── */}
      <div className="space-y-3">
        {clients.map((client, idx) => {
          const isActive = idx === activeIdx && client.statut === 'en_cours';
          const isNext = idx === activeIdx && client.statut === 'en_attente' && isFullyStarted;
          const isDone = client.statut === 'termine';
          // 🆕 Phase C5 — client non réalisé finalement (revient dans "à planifier")
          const isNonRealise = client.statut === 'non_realise' || client.non_realise === true;
          const isLocked = !isDone && !isNonRealise && !isActive && !isNext;
          // Adresse visible seulement si: admin OU (route démarrée ET client actif/terminé)
          const showAddress = isAdmin || ((isActive || isDone) && isFullyStarted);

          return (
            <Card key={client.id || idx} className={`transition-all ${isActive ? 'ring-2 ring-blue-500 shadow-lg' : isDone ? 'opacity-70' : isNonRealise ? 'opacity-60 border-red-300 bg-red-50/30' : isLocked ? 'opacity-50' : ''}`} data-testid={`route-client-${idx}`}>
              <CardContent className="py-4 px-3 sm:px-6">
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${isDone ? 'bg-green-500 text-white' : isNonRealise ? 'bg-red-200 text-red-700' : isActive ? 'bg-blue-500 text-white animate-pulse' : 'bg-gray-200 text-gray-600'}`}>
                    {isDone ? <CheckCircle className="w-4 h-4" /> : isNonRealise ? '✕' : idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-semibold text-sm sm:text-base ${isDone ? 'line-through text-gray-400' : isNonRealise ? 'line-through text-red-500' : ''}`}>
                      {client.client_nom}
                      {isNonRealise && (
                        <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-red-700 bg-red-100 border border-red-300 rounded px-1.5 py-0.5 align-middle" data-testid={`badge-non-realise-${idx}`}>
                          ❌ Non réalisé
                        </span>
                      )}
                    </p>
                    {showAddress ? (
                      <a
                        href={`https://maps.google.com/?q=${encodeURIComponent(client.adresse)}`}
                        target="_blank" rel="noopener noreferrer"
                        className="text-xs sm:text-sm text-blue-600 flex items-center gap-1 mt-1 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MapPin className="w-3 h-3 shrink-0" />
                        <span className="truncate">{client.adresse}</span>
                      </a>
                    ) : (
                      <p className="text-xs sm:text-sm text-gray-400 italic mt-1">
                        {!isFullyStarted ? "Adresse visible quand tous les employés auront commencé" : "Adresse visible après le client précédent"}
                      </p>
                    )}
                    {isDone && client.duree_minutes && (
                      <p className="text-xs text-green-600 mt-1 flex items-center gap-1"><Clock className="w-3 h-3" />{client.duree_minutes} min</p>
                    )}
                    {/* 🆕 Bloc "À FAIRE" — bien visible pour l'équipe terrain (avant les notes) */}
                    {!isDone && (client.nacelle_lavage_requise || client.echafauds_a_monter || client.nettoyer_echelle) && (
                      <div className="mt-2 p-2.5 bg-amber-50 rounded-md border-2 border-amber-400 shadow-sm">
                        <p className="text-xs font-bold text-amber-900 mb-1.5 uppercase tracking-wide">⚠️ À FAIRE SUR PLACE</p>
                        <div className="space-y-1">
                          {client.nacelle_lavage_requise && (
                            <div className="flex items-center gap-2 text-sm font-medium bg-white rounded px-2 py-1">
                              <span className="text-cyan-700">
                                🏗 Nacelle requise
                                {client.nacelle_lavage_nom ? (
                                  <> : <span className="font-semibold">{client.nacelle_lavage_nom}</span></>
                                ) : null}
                              </span>
                            </div>
                          )}
                          {client.echafauds_a_monter && (
                            <div className="flex flex-col gap-1 bg-white rounded px-2 py-1">
                              <div className="flex items-center gap-2 text-sm font-medium">
                                <span className="text-orange-700">🔧 Échafauds à monter</span>
                              </div>
                              {client.echafauds_utilises && client.echafauds_utilises.length > 0 && (
                                <div className="ml-5 flex flex-wrap gap-1">
                                  {client.echafauds_utilises.map((echafaud, eIdx) => (
                                    <span key={eIdx} className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded border border-orange-200">
                                      {echafaud.type} {echafaud.dimensions} × {echafaud.quantite}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                          {client.nettoyer_echelle && (
                            <div className="flex items-center gap-2 text-sm font-medium bg-white rounded px-2 py-1">
                              <span className="text-purple-700">
                                🪜 Nettoyer à l'échelle
                                {client.nettoyer_echelle_type_pieds ? (
                                  <> : <span className="font-semibold">Échelle {client.nettoyer_echelle_type_pieds} pieds</span></>
                                ) : null}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {!isDone && client.notes_lavage_pression && (
                      <div className="mt-2 bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
                        <p className="text-xs font-semibold text-blue-700 mb-0.5">Notes lavage :</p>
                        <p className="text-sm text-blue-900">{client.notes_lavage_pression}</p>
                      </div>
                    )}
                    {/* Bouton Voir BT — toujours accessible (sans adresse) */}
                    {client.dossier_id && !isDone && (
                      <Button variant="ghost" size="sm" onClick={() => voirBT(client.dossier_id)}
                        className="mt-2 text-xs text-indigo-600 hover:bg-indigo-50 h-7 px-2" data-testid={`btn-voir-bt-${idx}`}>
                        <FileText className="w-3 h-3 mr-1" /> Voir le bon de travail
                      </Button>
                    )}
                    <div className="mt-2 sm:hidden">
                      {isNext && (
                        <Button onClick={() => commencerNettoyage(idx)} disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 h-11">
                          <Navigation className="w-4 h-4 mr-2" /> Commencer ce client
                        </Button>
                      )}
                      {isActive && (
                        <Button onClick={() => terminerNettoyage(idx)} disabled={loading} className="w-full bg-green-600 hover:bg-green-700 h-11">
                          <CheckCircle className="w-4 h-4 mr-2" /> Terminer ce client
                        </Button>
                      )}
                      {/* 🐛 2026-05-09 — Le bouton « Non réalisé » est aussi disponible
                          sur un client DÉJÀ terminé (annulation après coup). Avant : !isDone */}
                      {isAdmin && !isNonRealise && (
                        <Button
                          onClick={() => marquerNonRealise(idx, client.client_nom)}
                          disabled={loading}
                          variant="outline"
                          className="w-full mt-2 border-red-300 text-red-700 hover:bg-red-50 h-10"
                          data-testid={`btn-non-realise-${idx}`}
                        >
                          {isDone ? '↩ Annuler le lavage (marquer non réalisé)' : '❌ Non réalisé finalement'}
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="hidden sm:block shrink-0">
                    {isNext && (
                      <Button onClick={() => commencerNettoyage(idx)} disabled={loading} size="sm" className="bg-blue-600 hover:bg-blue-700">
                        <Navigation className="w-3 h-3 mr-1" /> Commencer
                      </Button>
                    )}
                    {isActive && (
                      <Button onClick={() => terminerNettoyage(idx)} disabled={loading} size="sm" className="bg-green-600 hover:bg-green-700">
                        <CheckCircle className="w-3 h-3 mr-1" /> Terminer
                      </Button>
                    )}
                    {/* 🐛 2026-05-09 — Disponible aussi si déjà terminé (annulation après coup) */}
                    {isAdmin && !isNonRealise && (
                      <Button
                        onClick={() => marquerNonRealise(idx, client.client_nom)}
                        disabled={loading}
                        variant="outline"
                        size="sm"
                        className="ml-2 border-red-300 text-red-700 hover:bg-red-50"
                        data-testid={`btn-non-realise-desktop-${idx}`}
                      >
                        {isDone ? '↩ Annuler' : '❌ Non réalisé'}
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Dialog BT Preview (sans adresse) */}
      <Dialog open={showBtDialog} onOpenChange={setShowBtDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Bon de Travail — {btPreview?.client_nom}</DialogTitle></DialogHeader>
          {btPreview && (
            <div className="space-y-3">
              {/* 🆕 2026-05-18 — Helper local pour rendre du HTML (issu du TipTap) ou
                  du texte brut (legacy avec \n) de façon sûre. */}
              {(() => {
                const renderRich = (s) => {
                  if (!s) return '';
                  return /<[a-z][\s\S]*>/i.test(s) ? s : String(s).replace(/\r?\n/g, '<br>');
                };
                return (
                  <>
                    {btPreview.description_travaux && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase">Description des travaux</p>
                        <div
                          className="text-sm mt-1 prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: renderRich(btPreview.description_travaux) }}
                        />
                      </div>
                    )}
                    {btPreview.notes_speciales && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <p className="text-xs font-semibold text-amber-700">Notes spéciales</p>
                        <div
                          className="text-sm text-amber-900 mt-0.5 prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: renderRich(btPreview.notes_speciales) }}
                        />
                      </div>
                    )}
                    {btPreview.notes_lavage_pression && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-xs font-semibold text-blue-700">Notes lavage à pression</p>
                        <div
                          className="text-sm text-blue-900 mt-0.5 prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: renderRich(btPreview.notes_lavage_pression) }}
                        />
                      </div>
                    )}
                  </>
                );
              })()}
              {btPreview.zones?.length > 0 && (
                <div><p className="text-xs font-semibold text-gray-500 uppercase">Zones</p>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {btPreview.zones.map((z, i) => <Badge key={z.id || z.nom || `zone-${i}`} variant="outline" className="text-xs">{z.nom || z}</Badge>)}
                  </div></div>
              )}
              {btPreview.etapes?.length > 0 && (
                <div><p className="text-xs font-semibold text-gray-500 uppercase">Étapes</p>
                  <div className="space-y-1 mt-1">
                    {btPreview.etapes.map((et, i) => (
                      <div key={et.id || et._id || `${JSON.stringify(et)}-${i}`} className="flex items-center gap-2 text-sm">
                        <span className="w-5 h-5 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-xs font-bold">{i+1}</span>
                        <span>{et.nom || et.description || et}</span>
                      </div>
                    ))}
                  </div></div>
              )}
              {btPreview.produits?.length > 0 && (
                <div><p className="text-xs font-semibold text-gray-500 uppercase">Produits</p>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {btPreview.produits.map((p, i) => <Badge key={p.id || p.nom || `produit-${i}`} variant="secondary" className="text-xs">{p.nom || p}</Badge>)}
                  </div></div>
              )}
              <div className="grid grid-cols-2 gap-3 bg-gray-50 rounded-lg p-3">
                <div><p className="text-xs text-gray-500">Heures budgétées</p><p className="text-lg font-bold">{btPreview.heures_budgetees || 0}h</p></div>
                <div><p className="text-xs text-gray-500">Heures réelles</p><p className="text-lg font-bold">{btPreview.heures_reelles || 0}h</p></div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* 🆕 [5.5] Dialog confirmation nacelle retournée — apparaît à la fin de la dernière étape */}
      <Dialog open={nacelleRetourPrompt.open} onOpenChange={(o) => { if (!o) setNacelleRetourPrompt({ open: false, routeId: null }); }}>
        <DialogContent data-testid="dialog-nacelle-retournee">
          <DialogHeader>
            <DialogTitle>🏗️ La nacelle a-t-elle été retournée au bureau ?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Tous les clients de cette route ont été nettoyés. Confirmez si la nacelle est retournée afin d'arrêter les SMS de rappel et libérer son créneau dans le calendrier.
          </p>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => confirmerNacelleRetour(false)} data-testid="btn-nacelle-pas-encore">
              Pas encore
            </Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => confirmerNacelleRetour(true)} data-testid="btn-nacelle-retournee-oui">
              Oui, retournée
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

// ─── Carte Réparation des punches orphelins (admin) ───────────────────────────
// Permet à l'admin de réparer visuellement les punches "Route lavage" des
// routes historiques où aucun BT n'avait été lié. Analyse d'abord, puis applique.
function ReparationOrphelinsCard() {
  const [analyse, setAnalyse] = useState(null); // { total_orphans, total_reparables, details }
  const [apercu, setApercu] = useState(null);   // résultat du dry-run
  const [loading, setLoading] = useState(false);
  const [hasRun, setHasRun] = useState(false);

  const analyser = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/crm/routes-lavage/migration/orphan-punches`);
      setAnalyse(r.data);
      setHasRun(true);
    } catch (e) {
      toast.error('Erreur d\'analyse');
    } finally {
      setLoading(false);
    }
  };

  const previsualiser = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/crm/routes-lavage/migration/repair-orphan-punches?dry_run=true`);
      setApercu(r.data);
    } catch (e) {
      toast.error('Erreur d\'aperçu');
    } finally {
      setLoading(false);
    }
  };

  const appliquer = async () => {
    if (!window.confirm("Confirmer la réparation ? Cela créera un punch par client nettoyé et supprimera les punches orphelins 'Route lavage'. Cette action est irréversible.")) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/crm/routes-lavage/migration/repair-orphan-punches`);
      toast.success(`✅ Réparation terminée : ${r.data.total_nouveaux_punches} nouveaux punches créés sur ${r.data.total_orphans_traites} orphelins traités.`);
      setAnalyse(null);
      setApercu(null);
      setHasRun(false);
      // Rafraîchir l'analyse après un court délai
      setTimeout(analyser, 500);
    } catch (e) {
      toast.error('Erreur lors de la réparation');
    } finally {
      setLoading(false);
    }
  };

  // Initial: pas d'appel auto pour éviter du bruit si pas nécessaire
  if (!hasRun) {
    return (
      <Card className="mb-4 border-dashed border-slate-300 bg-slate-50" data-testid="card-reparation-orphelins">
        <CardContent className="py-3 flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-700">🔧 Historique des routes de lavage</p>
            <p className="text-xs text-slate-500">Vérifier et réparer les punches d'anciennes routes non liés à des BT</p>
          </div>
          <Button size="sm" variant="outline" onClick={analyser} disabled={loading} data-testid="btn-analyser-orphelins">
            {loading ? 'Analyse…' : 'Analyser'}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const nbOrphelins = analyse?.total_orphans || 0;
  const nbReparables = analyse?.total_reparables || 0;

  if (nbOrphelins === 0) {
    return (
      <Card className="mb-4 border-green-300 bg-green-50" data-testid="card-reparation-orphelins-ok">
        <CardContent className="py-3 flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-green-600" />
          <span className="text-sm font-semibold text-green-800">✅ Aucun punch orphelin — tout est en ordre !</span>
          <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setHasRun(false)} data-testid="btn-recommencer-analyse">Recommencer</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-4 border-2 border-orange-300 bg-orange-50" data-testid="card-reparation-orphelins-found">
      <CardContent className="py-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-600" />
            <div>
              <p className="text-sm font-bold text-orange-900">
                {nbOrphelins} punch{nbOrphelins > 1 ? 'es' : ''} orphelin{nbOrphelins > 1 ? 's' : ''} détecté{nbOrphelins > 1 ? 's' : ''} — {nbReparables} réparable{nbReparables > 1 ? 's' : ''}
              </p>
              <p className="text-xs text-orange-700">
                Routes historiques où les heures n'ont pas été correctement associées aux clients
              </p>
            </div>
          </div>
        </div>

        {/* Détails des orphelins */}
        {analyse?.details?.length > 0 && (
          <div className="bg-white rounded-lg border border-orange-200 mb-3 max-h-64 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-orange-100 sticky top-0">
                <tr>
                  <th className="px-2 py-1.5 text-left">Employé</th>
                  <th className="px-2 py-1.5 text-left">Date</th>
                  <th className="px-2 py-1.5 text-left">Clients nettoyés</th>
                  <th className="px-2 py-1.5 text-center">État</th>
                </tr>
              </thead>
              <tbody>
                {analyse.details.map((d, i) => (
                  <tr key={d.punch_id || i} className="border-b border-orange-100">
                    <td className="px-2 py-1.5 font-medium">{d.employee_nom || '—'}</td>
                    <td className="px-2 py-1.5 text-slate-500">
                      {d.punch_in_time ? new Date(d.punch_in_time).toLocaleDateString('fr-CA') : '—'}
                    </td>
                    <td className="px-2 py-1.5">
                      {d.clients_nettoyes?.length > 0
                        ? d.clients_nettoyes.map(c => `${c.nom_client} (${Math.round(c.duree_minutes)} min)`).join(', ')
                        : <span className="text-slate-400 italic">Aucun (route incomplète)</span>}
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      {d.peut_etre_repare
                        ? <Badge className="bg-green-100 text-green-700 text-[10px]">Réparable</Badge>
                        : <Badge className="bg-slate-100 text-slate-500 text-[10px]">Ignoré</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Aperçu du dry-run */}
        {apercu && (
          <div className="bg-blue-50 border border-blue-300 rounded-lg p-3 mb-3">
            <p className="text-sm font-bold text-blue-900 mb-2">👁️ Aperçu — Ce qui sera créé :</p>
            <p className="text-xs text-blue-700 mb-2">
              {apercu.total_nouveaux_punches} nouveaux punches seront créés sur {apercu.total_orphans_traites} orphelins.
            </p>
            <div className="max-h-40 overflow-y-auto space-y-1 text-xs">
              {(apercu.reparations || []).map((r, i) => (
                <div key={i} className="bg-white rounded p-2 border border-blue-200">
                  <p className="font-medium">{r.employee_nom} → {r.nb_punches_crees} punches</p>
                  <ul className="list-disc list-inside text-slate-600">
                    {(r.clients || []).map((c, j) => (
                      <li key={j}>
                        {c.nom} — <strong>{c.heures}h</strong>
                        {c.bt_id ? <span className="text-green-600 ml-1">(BT lié ✓)</span> : <span className="text-amber-600 ml-1">(pas de BT)</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Boutons action */}
        <div className="flex gap-2">
          {!apercu && nbReparables > 0 && (
            <Button size="sm" variant="outline" onClick={previsualiser} disabled={loading} data-testid="btn-previsualiser-reparation">
              {loading ? '...' : '👁️ Prévisualiser la réparation'}
            </Button>
          )}
          {apercu && (
            <Button
              size="sm"
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={appliquer}
              disabled={loading}
              data-testid="btn-appliquer-reparation"
            >
              {loading ? 'Réparation…' : `✅ Appliquer (${apercu.total_nouveaux_punches} nouveaux punches)`}
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => { setAnalyse(null); setApercu(null); setHasRun(false); }} data-testid="btn-fermer-reparation">
            Fermer
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
